// Billing constants shared across the app

// Valid currencies that can be selected by users and admins
// Single source of truth: currency code, name, symbol, exchange rate from USD, and Stripe decimal handling
// Exchange rates updated: January 2025 - should be updated periodically (monthly/quarterly)
// zeroDecimal: Stripe zero-decimal currencies (amount in whole units, not cents)
// https://docs.stripe.com/currencies#zero-decimal
const VALID_CURRENCIES_DATA = [
  { code: 'usd', name: 'US Dollar', symbol: '$', rate: 1.0 },
  { code: 'eur', name: 'Euro', symbol: '€', rate: 0.92 },
  { code: 'gbp', name: 'British Pound', symbol: '£', rate: 0.79 },
  { code: 'cad', name: 'Canadian Dollar', symbol: 'CA$', rate: 1.36 },
  { code: 'aud', name: 'Australian Dollar', symbol: 'A$', rate: 1.52 },
  { code: 'jpy', name: 'Japanese Yen', symbol: '¥', rate: 150.0, zeroDecimal: true },
  { code: 'chf', name: 'Swiss Franc', symbol: 'CHF', rate: 0.88 },
  { code: 'nzd', name: 'New Zealand Dollar', symbol: 'NZ$', rate: 1.64 },
  { code: 'sek', name: 'Swedish Krona', symbol: 'SEK', rate: 10.5 },
  { code: 'nok', name: 'Norwegian Krone', symbol: 'NOK', rate: 10.5 },
  { code: 'dkk', name: 'Danish Krone', symbol: 'DKK', rate: 6.9 },
  { code: 'pln', name: 'Polish Zloty', symbol: 'PLN', rate: 4.0 },
  { code: 'czk', name: 'Czech Koruna', symbol: 'CZK', rate: 23.0 },
  { code: 'huf', name: 'Hungarian Forint', symbol: 'HUF', rate: 360.0 },
  { code: 'ron', name: 'Romanian Leu', symbol: 'RON', rate: 4.6 },
  { code: 'bgn', name: 'Bulgarian Lev', symbol: 'BGN', rate: 1.8 },
  { code: 'try', name: 'Turkish Lira', symbol: 'TRY', rate: 34.0 },
  { code: 'inr', name: 'Indian Rupee', symbol: '₹', rate: 83.0 },
  { code: 'cny', name: 'Chinese Yuan', symbol: '¥', rate: 7.2 },
  { code: 'krw', name: 'South Korean Won', symbol: '₩', rate: 1330.0, zeroDecimal: true },
  { code: 'sgd', name: 'Singapore Dollar', symbol: 'S$', rate: 1.34 },
  { code: 'hkd', name: 'Hong Kong Dollar', symbol: 'HK$', rate: 7.8 },
  { code: 'mxn', name: 'Mexican Peso', symbol: 'MX$', rate: 17.0 },
  { code: 'brl', name: 'Brazilian Real', symbol: 'R$', rate: 5.0 },
  { code: 'zar', name: 'South African Rand', symbol: 'R', rate: 19.0 },
  { code: 'ils', name: 'Israeli Shekel', symbol: '₪', rate: 3.7 },
  { code: 'aed', name: 'UAE Dirham', symbol: 'AED', rate: 3.67 },
  { code: 'sar', name: 'Saudi Riyal', symbol: 'SAR', rate: 3.75 },
  // 'hrk': REMOVED - replaced by EUR in 2023
  // 'rub': REMOVED - sanctions/payment issues
];

// Create zero-decimal currency lookup
const ZERO_DECIMAL_CURRENCIES = new Set(
  VALID_CURRENCIES_DATA.filter(c => c.zeroDecimal).map(c => c.code)
);

/**
 * Check if a currency is zero-decimal (no cents/subunits)
 */
export function isZeroDecimalCurrency(currency) {
  return ZERO_DECIMAL_CURRENCIES.has(currency?.toLowerCase());
}

/**
 * Convert display amount to Stripe's smallest currency unit
 * Most currencies use cents (multiply by 100), but zero-decimal currencies don't
 * @param {number} amount - Display amount (e.g., 10.00 for $10, or 1000 for ¥1000)
 * @param {string} currency - Currency code
 * @returns {number} Amount in Stripe's smallest unit
 */
export function toStripeAmount(amount, currency) {
  return Math.round(isZeroDecimalCurrency(currency) ? amount : amount * 100);
}

/**
 * Convert Stripe's smallest currency unit to display amount
 * @param {number} stripeAmount - Amount in Stripe's smallest unit
 * @param {string} currency - Currency code
 * @returns {number} Display amount
 */
export function fromStripeAmount(stripeAmount, currency) {
  return isZeroDecimalCurrency(currency) ? stripeAmount : stripeAmount / 100;
}

// Export currencies array for UI selection (without rate)
export const CURRENCIES = VALID_CURRENCIES_DATA.map(({ code, name, symbol }) => ({
  code,
  name,
  symbol,
}));

// Create exchange rate lookup map
const USD_TO_CURRENCY_RATES = Object.fromEntries(
  VALID_CURRENCIES_DATA.map(({ code, rate }) => [code, rate])
);

// Helper function to get currency name from code
export const getCurrencyName = (code) => {
  const currency = CURRENCIES.find(c => c.code === code?.toLowerCase());
  return currency?.name || code?.toUpperCase() || 'USD';
};

// Helper function to get currency symbol from code
export const getCurrencySymbol = (code) => {
  const currency = CURRENCIES.find(c => c.code === code?.toLowerCase());
  return currency?.symbol || '$';
};

/**
 * Convert an amount from a foreign currency to USD
 * @param {number} amount - Amount in the foreign currency
 * @param {string} currency - Currency code (e.g., 'usd', 'eur', 'gbp', 'inr')
 * @returns {number} Amount in USD
 */
export function convertToUSD(amount, currency = 'usd') {
  const normalizedCurrency = currency?.toLowerCase() || 'usd';
  if (normalizedCurrency === 'usd') {
    return amount;
  }
  const rate = USD_TO_CURRENCY_RATES[normalizedCurrency];
  if (!rate) {
    return amount; // Fallback to original amount if currency not found
  }
  // Rate represents how many units of foreign currency = 1 USD
  // So to convert to USD, divide by the rate
  return amount / rate;
}

